import Link from 'next/link'

import { Loading, RankMedal } from 'app/components/atoms'

import AccountIcon from 'public/icons/account.svg'
import PointIcon from 'public/icons/point.svg'

import formatToPoint from 'utils/formatToPoint'
import { useTranslation } from 'app/i18n/client'
import { useParams } from 'next/navigation'

interface AccountInfoProps {
  userName: string
  honorific: string
  rankNumber?: number
  rankName?: string
  pointInfo: {
    isLoading?: boolean
    isError?: boolean
    totalPoint?: number
  }
  pointExpiry?: {
    isSuccess?: boolean
    within60Days?: number
  }
}

const AccountInfo: React.FunctionComponent<AccountInfoProps> = ({
  userName,
  honorific,
  rankNumber,
  rankName,
  pointInfo,
  pointExpiry,
}) => {
  const { t } = useTranslation()
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}

  const { isLoading, isError, totalPoint } = pointInfo
  const showExpiryLink = Boolean(
    pointExpiry?.isSuccess && (pointExpiry?.within60Days ?? 0) > 0,
  )

  return (
    <div className="flex items-center gap-8">
      <Link
        prefetch={false}
        href={`/${locale}/my-page/profile`}
        className="flex items-center space-x-2"
      >
        <AccountIcon className="fill-current text-dark" />
        <span className="font-light text-base">
          {userName}
          {honorific && <span className="text-xs">{honorific}</span>}
        </span>
      </Link>
      <div className="h-3.5 border-l border-gray-150" />
      <RankMedal rankNumber={rankNumber} rankName={rankName} />
      <Link
        prefetch={false}
        href={`/${locale}/my-page/point-information`}
        className="flex flex-col gap-1"
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-2">
            <PointIcon className="w-5 h-5 stroke-current text-gray [&_g]:opacity-100" />
            <span className="text-base font-light leading-[1.6] text-gray">
              {t('Points Balance')}
            </span>
          </div>
          {isLoading ? (
            <div className="w-12 h-8 flex items-center justify-center">
              <Loading className="!text-white !opacity-60" size="sm" />
            </div>
          ) : isError ? (
            <div>N/A</div>
          ) : (
            <div className="font-lora text-lg font-normal leading-none text-primary">
              {formatToPoint(totalPoint)}
            </div>
          )}
        </div>
        {showExpiryLink && (
          <span className="text-xs font-light leading-[1.6] text-primary underline [text-underline-position:from-font]">
            {t('You have points expiring within 60 days')}
          </span>
        )}
      </Link>
    </div>
  )
}
export default AccountInfo
